import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { queryClient } from '@/api/queryClient';
import { authApi } from './api';
import { getHttpStatus, setUnauthorizedListener } from './authState';
import { clearCsrfToken, setCsrfToken } from './csrf';
import type { AuthContextValue, AuthSession, AuthState, CurrentUser, LoginCredentials } from './types';

const AuthContext = createContext<AuthContextValue | null>(null);

const initialState: AuthState = {
  status: 'loading',
  currentUser: null,
  csrfToken: null,
  error: null,
};

function userHasCapability(user: CurrentUser | null, capability: string): boolean {
  if (!user) {
    return false;
  }

  return user.capabilities.includes('*') || user.capabilities.includes(capability);
}

export function clearAuthSessionState(): void {
  clearCsrfToken();
  queryClient.clear();
}

export function AuthErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div style={{ marginTop: 100, textAlign: 'center' }}>
      <p>{message}</p>
      <button type="button" onClick={onRetry}>
        重试
      </button>
    </div>
  );
}

interface AuthProviderProps {
  children: ReactNode;
  loginFallback?: ReactNode;
}

export function AuthProvider({ children, loginFallback }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(initialState);

  const applySession = useCallback((session: AuthSession): CurrentUser => {
    setCsrfToken(session.csrfToken);
    setState({
      status: 'authenticated',
      currentUser: session.currentUser,
      csrfToken: session.csrfToken,
      error: null,
    });
    return session.currentUser;
  }, []);

  const markAnonymous = useCallback((error: string | null = null) => {
    clearAuthSessionState();
    setState({
      status: 'anonymous',
      currentUser: null,
      csrfToken: null,
      error,
    });
  }, []);

  const markAuthError = useCallback((error: string) => {
    clearAuthSessionState();
    setState({
      status: 'error',
      currentUser: null,
      csrfToken: null,
      error,
    });
  }, []);

  const refresh = useCallback(async (): Promise<CurrentUser | null> => {
    try {
      return applySession(await authApi.me());
    } catch (error) {
      const status = getHttpStatus(error);
      if (status === 401) {
        markAnonymous();
      } else {
        markAuthError('登录状态校验失败');
      }
      return null;
    }
  }, [applySession, markAnonymous, markAuthError]);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<CurrentUser> =>
      applySession(await authApi.login(credentials)),
    [applySession],
  );

  const logout = useCallback(async (): Promise<void> => {
    await authApi.logout();
    markAnonymous();
  }, [markAnonymous]);

  useEffect(() => {
    let cancelled = false;

    const loadCurrentUser = async () => {
      try {
        const session = await authApi.me();
        if (!cancelled) {
          applySession(session);
        }
      } catch (error) {
        if (!cancelled) {
          const status = getHttpStatus(error);
          if (status === 401) {
            markAnonymous();
          } else {
            markAuthError('登录状态校验失败');
          }
        }
      }
    };

    void loadCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [applySession, markAnonymous, markAuthError]);

  useEffect(
    () =>
      setUnauthorizedListener(() => {
        markAnonymous();
      }),
    [markAnonymous],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      currentUser: state.currentUser,
      isAuthenticated: state.status === 'authenticated',
      isLoading: state.status === 'loading',
      login,
      logout,
      refresh,
      hasCapability: (capability: string) => userHasCapability(state.currentUser, capability),
    }),
    [login, logout, refresh, state],
  );

  if (state.status === 'loading') {
    return (
      <div style={{ marginTop: 100, textAlign: 'center' }}>
        正在校验登录状态
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {state.status === 'error' ? (
        <AuthErrorState
          message={state.error ?? '登录状态校验失败'}
          onRetry={() => {
            void refresh();
          }}
        />
      ) : state.status === 'anonymous' ? (
        loginFallback ?? children
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return value;
}

export function hasCapability(user: CurrentUser | null, capability: string): boolean {
  return userHasCapability(user, capability);
}
